import { CoreModule } from '@abp/ng.core';
import { registerLocale, storeLocaleData } from '@abp/ng.core/locale';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { InternetConnectionStatusComponent, ThemeSharedModule } from '@abp/ng.theme.shared';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommercialUiConfigModule } from '@shared/commercial-ui/config';
import { AccountConfigModule } from '@abp/ng.account/config';
import { IdentityConfigModule } from '@abp/ng.identity/config';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { ThemeLeptonXModule } from '@abp/ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@abp/ng.theme.lepton-x/layouts';
import { CITIES_CITY_ROUTE_PROVIDER } from './cities/city/providers/city-route.provider';
import { CATEGORIES_CATEGORY_ROUTE_PROVIDER } from './categories/category/providers/category-route.provider';
import { SUB_CATEGORIES_SUB_CATEGORY_ROUTE_PROVIDER } from './sub-categories/sub-category/providers/sub-category-route.provider';
import { PLACES_PLACE_ROUTE_PROVIDER } from './places/place/providers/place-route.provider';
import { BUSINESS_TYPES_BUSINESS_TYPE_ROUTE_PROVIDER } from './business-types/business-type/providers/business-type-route.provider';
import { REQUESTED_ITEMS_REQUESTED_ITEM_ROUTE_PROVIDER } from './requested-items/requested-item/providers/requested-item-route.provider';
import { STORED_ITEMS_STORED_ITEM_ROUTE_PROVIDER } from './stored-items/stored-item/providers/stored-item-route.provider';
import { PARTNERS_PARTNER_ROUTE_PROVIDER } from './partners/partner/providers/partner-route.provider';
import { ADMINS_ADMIN_ROUTE_PROVIDER } from './admins/admin/providers/admin-route.provider';
import { USERS_USER_ROUTE_PROVIDER } from './users/user/providers/user-route.provider';
import { BRANCHES_BRANCH_ROUTE_PROVIDER } from './branches/branch/providers/branch-route.provider';
import { FEES_FEE_ROUTE_PROVIDER } from './fees/fee/providers/fee-route.provider';
import { LANDING_ROUTE_PROVIDER } from './home/providers/landing-route.provider';
import { REQUESTED_CLAIMS_REQUESTED_CLAIM_ROUTE_PROVIDER } from './requested-claims/requested-claim/providers/requested-claim-route.provider';
import { STORED_ITEMS_DETAILS_ROUTE_PROVIDER } from './stored-items-details/providers/stored-items-details-route.provider';
import { MANAGE_PROFILE_TAB_PROVIDER } from './profile/manage-profile-tabs.provider';
import { REGISTER_COMPONENT_PROVIDER } from './account/register.provider';
import { SHIPMENTS_SHIPMENT_ROUTE_PROVIDER } from './shipments/shipment/providers/shipment-route.provider';
import { FREQUENTLY_ASKED_QUESTIONS_FREQUENTLY_ASKED_QUESTION_ROUTE_PROVIDER } from './frequently-asked-questions/frequently-asked-question/providers/frequently-asked-question-route.provider';
import { MATCHES_MATCH_ROUTE_PROVIDER } from './matches/match/providers/match-route.provider';
import { HELP_CENTER_VIEW_ROUTE_PROVIDER } from './help-center-view/providers/help-center-view-route.provider';
import { TOPICS_TOPIC_ROUTE_PROVIDER } from './topics/topic/providers/topic-route.provider';
import { SECURITY_LOGS_ROUTE_PROVIDER } from './security-logs/providers/security-log-route.provider';
import { REFERRERS_ROUTE_PROVIDER } from './referrers/providers/referrer-route.provider';
import { PARTNER_LEADS_ROUTE_PROVIDER } from './partner-leads/providers/partner-lead-route.provider';
import { SIDEBAR_LOGO_PROVIDER } from './sidebar-logo/sidebar-logo.provider';
import { MOBILE_NAVBAR_PROVIDER } from './mobile-navbar/mobile-navbar.provider';
import { GeneralSettingsComponent } from './general-settings/general-settings.component';
import { Notification } from "./notification/notification.component";

// registerLocale() resolves Angular's locale data with a fully dynamic
// `import(`@angular/common/locales/${locale}`)`. The Angular 17 esbuild/vite pipeline cannot
// statically analyse that specifier, so the request 404s to the dev-server index page and ABP
// swallows it as "Cannot find the ar locale file". Angular is then left with no locale data and
// every LOCALE_ID-driven format throws NG0701 — including NgbDatepickerI18nDefault, which builds
// its month and weekday names in its constructor, so no datepicker in the app could be opened.
//
// registerLocaleData puts the data straight into the table getLocaleMonthNames() reads, so it holds
// even when ABP's own resolution fails. storeLocaleData additionally feeds ABP's error handler, so
// its registerLocaleFn resolves with real data instead of undefined and stops logging a failure.
// Both are static imports and survive the dev server as well as the AOT build.
// One line per culture the backend actually ships (en.json and ar.json).
registerLocaleData(localeAr, 'ar');
registerLocaleData(localeEn, 'en');
storeLocaleData(localeAr, 'ar');
storeLocaleData(localeEn, 'en');

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
        environment,
        registerLocaleFn: registerLocale(),
    }),
    AbpOAuthModule.forRoot(),
    ThemeSharedModule.forRoot(),
    AccountConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    CommercialUiConfigModule.forRoot(),
    FeatureManagementModule.forRoot(),
    InternetConnectionStatusComponent,
    ThemeLeptonXModule.forRoot(),
    SideMenuLayoutModule.forRoot(),
    Notification,
    GeneralSettingsComponent
],
  providers: [
    APP_ROUTE_PROVIDER,
    SECURITY_LOGS_ROUTE_PROVIDER,
    REFERRERS_ROUTE_PROVIDER,
    PARTNER_LEADS_ROUTE_PROVIDER,
    CITIES_CITY_ROUTE_PROVIDER,
    CATEGORIES_CATEGORY_ROUTE_PROVIDER,
    SUB_CATEGORIES_SUB_CATEGORY_ROUTE_PROVIDER,
    PLACES_PLACE_ROUTE_PROVIDER,
    BUSINESS_TYPES_BUSINESS_TYPE_ROUTE_PROVIDER,
    REQUESTED_ITEMS_REQUESTED_ITEM_ROUTE_PROVIDER,
    STORED_ITEMS_STORED_ITEM_ROUTE_PROVIDER,
    REQUESTED_CLAIMS_REQUESTED_CLAIM_ROUTE_PROVIDER,
    PARTNERS_PARTNER_ROUTE_PROVIDER,
    ADMINS_ADMIN_ROUTE_PROVIDER,
    USERS_USER_ROUTE_PROVIDER,
    BRANCHES_BRANCH_ROUTE_PROVIDER,
    FEES_FEE_ROUTE_PROVIDER,
    LANDING_ROUTE_PROVIDER,
    STORED_ITEMS_DETAILS_ROUTE_PROVIDER,
    MANAGE_PROFILE_TAB_PROVIDER,
    REGISTER_COMPONENT_PROVIDER,
    SHIPMENTS_SHIPMENT_ROUTE_PROVIDER,
    FREQUENTLY_ASKED_QUESTIONS_FREQUENTLY_ASKED_QUESTION_ROUTE_PROVIDER,
    MATCHES_MATCH_ROUTE_PROVIDER,
    HELP_CENTER_VIEW_ROUTE_PROVIDER,
    TOPICS_TOPIC_ROUTE_PROVIDER,
    SIDEBAR_LOGO_PROVIDER,
    MOBILE_NAVBAR_PROVIDER,
  ],
  schemas:[CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
